import { z } from 'zod';

import { DataSetSchema, VersionSchema, YesOrNoSchema } from './internal';

type IntegerStr = `${number}`;
const IntegerStrSchema = z.custom<IntegerStr>();
const MoneyCentsSchema = IntegerStrSchema;
export type ClaimResponseMoneyCents = z.infer<typeof MoneyCentsSchema>;

export const ClaimResponseClaimFinancialsSchema = z.object({
  /**
   * Total amount claimed for
   */
  TotalClaimedAmt: MoneyCentsSchema,
  /**
   * Total amount paid by medical aid
   */
  TotalPaidProvAmt: MoneyCentsSchema,
  /**
   * Outstanding amount
   */
  BalanceOutstanding: MoneyCentsSchema,
});

export type ClaimResponseClaimFinancials = z.infer<
  typeof ClaimResponseClaimFinancialsSchema
>;

/**
 * Get valid status codes and descriptions
 */

export const StatusCode = {
  AwaitingResponse: '2',
  ReceivedByHealthbridge: '3',
  ValidatedByHealthbridge: '4',
  ValidatedByFunder: '6',
  Processed: '7',
  Rejected: '8',
  AwaitingReversalResponse: '10',
  ReversalAccepted: '11',
  ReversalProcessed: '12',
  ReversalRejected: '13',
  PartRejected: '17',
  PartProcessedByMedicalAid: '21',
  Null: '99',
} as const;

export const StatusCodeSchema = z.nativeEnum(StatusCode);
export type StatusCodeString = z.infer<typeof StatusCodeSchema>;

/**
 * Defined as such to allow the value of the code to be used in place of the code
 * Ensures that test cases better reflect the data we receive from the API in the JSON samples
 */

const StatusDescSchema = z.string();

export type StatusDesc = z.infer<typeof StatusDescSchema>;

export const ClaimResponseLineItemFinancialsSchema = z.object({
  /**
   * What was the claimed amount
   */
  BilledAmt: MoneyCentsSchema,
  /**
   * What the medical aid will pay
   */
  MAPayable: MoneyCentsSchema,
  /**
   * Scheme rate for the item claimed
   */
  MedicalAidRate: MoneyCentsSchema.optional(),
});

export type ClaimResponseLineItemFinancials = z.infer<
  typeof ClaimResponseLineItemFinancialsSchema
>;

export const ResponseSchema = z.object({
  /**
   * Reason - Explanation of the payment being handled. This can indicate a valid claim that doesn't get paid
   * Error - Something wrong with claim
   */
  '@Type': z.union([z.literal('Reason'), z.literal('Error')]),
  /**
   * Code comes directly from the medical aid
   */
  Code: IntegerStrSchema,
  /**
   * Directly from the medical aid
   */
  Desc: z.string(),
});

export type Response = z.infer<typeof ResponseSchema>;

export const ClaimResponseLineItemSchema = z.object({
  UniqueID: IntegerStrSchema,
  StatusCode: StatusCodeSchema,
  StatusDesc: StatusDescSchema,
  MALineRefNum: z.string().optional(),
  Editable: YesOrNoSchema,
  Reversible: YesOrNoSchema,
  PayWho: z.literal('Provider'),
  LineItemFinancials: ClaimResponseLineItemFinancialsSchema,
  Responses: ResponseSchema.array(),
});

export type ClaimResponseLineItem = z.infer<typeof ClaimResponseLineItemSchema>;

export const ClaimResponseDetailsSchema = z.object({
  ClaimFinancials: ClaimResponseClaimFinancialsSchema,
  LineItems: ClaimResponseLineItemSchema.array(),
  Responses: ResponseSchema.array().optional(),
});

export type ClaimResponseDetails = z.infer<typeof ClaimResponseDetailsSchema>;

export type ClaimResponseTransaction = z.infer<
  typeof ClaimResponseTransactionSchema
>;

export const ClaimResponseTransactionSchema = z.object({
  ReferenceNum: z.string(),
  DataSet: DataSetSchema,
  InvoiceNum: z.string(),
});

export const ClaimSchema = z.object({
  Transaction: ClaimResponseTransactionSchema,
  Details: ClaimResponseDetailsSchema,
  Disclaimer: z.string().optional(),
});

export type Claim = z.infer<typeof ClaimSchema>;

export const ClaimResponseSchema = z.object({
  MedicalAidMessage: z.object({
    '@Version': VersionSchema,
    Claim: ClaimSchema,
  }),
});

export type ClaimResponse = z.infer<typeof ClaimResponseSchema>;
